import { Col, Row } from 'antd';
import { useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';
import { type BankFormValues, BankIntegration, CreateBankDetailsForm, FinanceProductType } from '.';
import { useGetModuleSpecsQuery } from '../../../../api/queries/getModuleSpecs';
import {
    type BankIntegrationProvider,
    type BankIntegrationSettings,
    DbsPayloadScheme,
    MaybankEnvironment,
    type SystemBank,
} from '../../../../api/types';

export type NewBankFormValues = BankFormValues & {
    availableFinanceProductTypes: SystemBank['availableFinanceProductTypes'];
    showMarginOfFinance: boolean;
    integrationProvider: BankIntegrationProvider;
    integrationSettings: BankIntegrationSettings;
};

// eslint-disable-next-line max-len
const DEFAULT_REMOTE_ACKNOWLEDGMENT_INFO = `Your finance application has been submitted and you will receive shortly a SMS link from Default to provide supporting documents and information.
Should you not receive any SMS or have any questions, please contact us at:`;

export const defaultIntegrationSettings: BankIntegrationSettings = {
    email: { email: '' },
    hlf: {
        allowedIps: '0.0.0.0/0',
        baseUrl: '',
        submissionPartial: '',
        editionPartial: '',
        cancellationPartial: '',
        reassignPartial: '',
        privateKey: '',
        publicCert: '',
        dealerCode: '',
    },
    hlfV2: {
        allowedIps: '0.0.0.0/0',
        baseUrl: '',
        submissionPartial: '',
        editionPartial: '',
        cancellationPartial: '',
        reassignPartial: '',
        privateKey: '',
        publicCert: '',
        dealerCode: '',
    },
    uob: {
        allowedIps: '0.0.0.0/0',
        host: '',
        clientId: '',
        applicationId: '',
        apiKey: '',
        sslPrivateKey: '',
        sslPublicCert: '',
        encryptionPrivateKey: '',
        encryptionPublicCert: '',
        signaturePrivateKey: '',
        dealerCode: '',
    },
    dbs: {
        allowedIps: '0.0.0.0/0',
        host: '',
        apiKey: '',
        orgId: '',
        privateKey: '',
        publicCert: '',
        mappings: [],
        payloadScheme: DbsPayloadScheme.V1,
        dealerCode: '',
        certCA: '',
    },
    bmwFs: {
        host: '',
        accessKey: '',
        secretKey: '',
        dealerCode: '',
    },
    maybank: {
        allowedIps: '0.0.0.0/0',
        dealerId: '',
        host: '',
        clientId: '',
        apiKey: '',
        environment: MaybankEnvironment.Sit,
        clientSecret: '',
        encryptionPrivateKey: '',
        encryptionPublicCert: '',
        signaturePrivateKey: '',
        signaturePublicCert: '',
        dealerCode: '',
    },
    enbd: {
        financeManager: {
            name: '',
            email: '',
        },
        companyName: '',
        dealerCode: '',
    },
};

export const defaultValues: NewBankFormValues = {
    displayName: '',
    legalName: {
        defaultValue: '',
        overrides: [],
    },
    moduleId: null,
    email: '',
    color: 'ef2c72',
    order: null,
    remoteFlowAcknowledgmentInfo: DEFAULT_REMOTE_ACKNOWLEDGMENT_INFO,
    financingDisclaimer: {
        defaultValue: [{ defaultValue: '', overrides: [] }],
        overrides: [],
    },
    submissionApprovalModuleId: null,
    reSubmissionApprovalModuleId: null,
    sendEmailToCustomer: false,
    sendPDFToCustomer: false,
    availableFinanceProductTypes: [],
    showMarginOfFinance: false,

    isActive: true,
    hasVSOUpload: false,
    hasUploadDocuments: false,

    integrationProvider: null,
    integrationSettings: defaultIntegrationSettings,

    estFee: 0,
    allowVariantCodeMapping: false,
    showCommentsField: false,
};

const CreateBankFrom = () => {
    const { values, setFieldValue } = useFormikContext<NewBankFormValues>();

    const { data: moduleData } = useGetModuleSpecsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            moduleId: values?.moduleId,
        },
        skip: !values?.moduleId,
    });

    // track latest company id
    const previousCompanyId = useRef(null);

    // clear selected modules if companyId change
    useEffect(() => {
        if (moduleData?.module?.companyId && previousCompanyId.current !== moduleData.module.companyId) {
            if (values.submissionApprovalModuleId) {
                setFieldValue('submissionApprovalModuleId', null);
            }

            if (values.reSubmissionApprovalModuleId) {
                setFieldValue('reSubmissionApprovalModuleId', null);
            }

            previousCompanyId.current = moduleData?.module?.companyId;
        }
    }, [moduleData?.module?.companyId, values, setFieldValue]);

    return (
        <Row gutter={[16, 16]}>
            <Col span={24}>
                <CreateBankDetailsForm module={moduleData?.module} />
            </Col>
            <Col xs={24}>
                <FinanceProductType isUpdate={false} />
            </Col>
            <Col span={24}>
                <BankIntegration provider={values.integrationProvider} />
            </Col>
        </Row>
    );
};

export default CreateBankFrom;
